"use client";

import { Hand, Info, MapPin, RefreshCw, UploadCloud, Users, Zap } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import USMapSVG from "@/app/(authenticated)/settings/_modules/components/USMapSVG";
import type { StateData } from "@/app/(authenticated)/settings/_modules/components/USMapSVG";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";

/**
 * Deep coaching analytics for one agent — powered by
 * GET /agencies/:id/agents/:agentId/performance-insights.
 * Sections: call KPIs · contact-rate-by-attempt · answer-rate by lead-local
 * hour · lead hygiene (untouched/stale/no-next-step) · lead-source results.
 */

// DialIQ peak dial windows (mirrors DIAL_WINDOWS in redial.service.ts — the
// hours where the DialIQ chips light up green). endHour is exclusive.
const DIALIQ_PEAK_WINDOWS = [
  { startHour: 8, endHour: 10 },   // AM
  { startHour: 15, endHour: 17 },  // PM
  { startHour: 19, endHour: 21 },  // Evening
];
const isDialIqPeak = (h: number) => DIALIQ_PEAK_WINDOWS.some((w) => h >= w.startHour && h < w.endHour);

// stage pill tones — used by the Lead Pipeline card's movement strip
export const stagePill = (stage: string) => {
  const l = stage.toLowerCase();
  if (l.includes("sold")) return "bg-emerald-100 text-emerald-700";
  if (l.includes("underwriting") || l === "uwr") return "bg-amber-100 text-amber-700";
  if (l.includes("appt") || l.includes("appointment")) return "bg-cyan-100 text-cyan-700";
  if (l.includes("new")) return "bg-blue-100 text-blue-700";
  if (l.includes("no sale") || l.includes("declined") || l.includes("do not") || l.includes("bad")) return "bg-red-100 text-red-600";
  return "bg-indigo-50 text-indigo-600";
};

/**
 * Animated "dialer pod" for the Dials-by-Mode card: a slowly spinning dashed
 * dial ring + a progress arc that eases in on mount, count in the middle.
 */
// 2485 -> "2.5k" so dial counts never crowd the ring; exact value
// stays available via the title tooltip
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
  : n >= 1_000 ? `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`
  : n.toLocaleString();

function DialerPod({
  label,
  count,
  total,
  ringClass,
  badgeClass,
  icon: Icon,
  delay,
  isTop,
}: {
  label: string;
  count: number;
  total: number;
  ringClass: string;
  badgeClass: string;
  icon: React.ComponentType<{ className?: string }>;
  delay: number;
  isTop: boolean;
}) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOn(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  const share = total > 0 ? count / total : 0;
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <div className={`relative flex flex-col items-center rounded-xl p-3 ring-1 ring-inset transition-shadow ${isTop ? "bg-gradient-to-b from-white to-gray-50 ring-gray-200 shadow-sm" : "bg-gray-50/60 ring-gray-100"}`}>
      {isTop && (
        <span className="absolute right-2 top-2 flex h-2 w-2">
          <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${badgeClass}`} />
          <span className={`relative inline-flex h-2 w-2 rounded-full ${badgeClass}`} />
        </span>
      )}
      <div className="relative h-20 w-20">
        {/* rotary-dial ring, slowly spinning */}
        <svg viewBox="0 0 64 64" className="absolute inset-0 h-full w-full animate-[spin_16s_linear_infinite] opacity-25">
          <circle cx="32" cy="32" r="31" fill="none" strokeWidth="1.5" strokeDasharray="2.5 6" className={ringClass} />
        </svg>
        {/* share arc, eases in on mount */}
        <svg viewBox="0 0 64 64" className="h-full w-full -rotate-90">
          <circle cx="32" cy="32" r={r} fill="none" strokeWidth="5" className="stroke-gray-200/70" />
          <circle
            cx="32" cy="32" r={r} fill="none" strokeWidth="5" strokeLinecap="round"
            className={`${ringClass} transition-all duration-1000 ease-out`}
            strokeDasharray={`${(on ? share : 0) * c} ${c}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center" title={count.toLocaleString()}>
          <span className={`font-bold tabular-nums text-gray-900 ${count >= 1_000 ? "text-base" : "text-lg"}`}>{fmtCompact(count)}</span>
          <span className="text-[9px] font-medium text-gray-400 tabular-nums">{(share * 100).toFixed(0)}%</span>
        </div>
        <span className={`absolute -top-1.5 left-1/2 -translate-x-1/2 rounded-full p-1 shadow-sm ${badgeClass}`}>
          <Icon className="h-3 w-3 text-white" />
        </span>
      </div>
      <p className="mt-1.5 text-[11px] font-semibold text-gray-700">{label}</p>
    </div>
  );
}

/** Small SVG donut gauge for the caller-ID health score. */
export interface Insights {
  core: { dials: number; dials_outbound: number; dials_inbound: number; conversations: number; talk_seconds: number; talk_seconds_outbound: number; talk_seconds_inbound: number; conversation_seconds: number; dialer_seconds: number; answered: number; answered_outbound: number; conversations_outbound: number; connected_20s: number; stuck_lines: number; unique_leads_dialed: number };
  sms?: { sms_sent: number; sms_received: number; sms_leads: number };
  dialerModes?: { powerDialer: number; multiLine: number; manual: number; total: number };
  pipeline?: { stage: string; count: number }[];
  geo?: { state: string; leads: number; policies: number; premium: number; calls: number }[];
  selfUploads?: {
    total: number;
    unassigned: number;
    self_assigned: number;
    assigned_away: number;
    last_upload?: string | null;
    assignedAwayTo?: { name: string; count: number }[];
    receivedFrom?: { name: string; count: number }[];
  };
  callerIdHealth?: {
    phone_number: string;
    health_score?: number;
    health_status?: string;
    answer_rate_30d?: string | number;
    spam_flag_count?: number;
    stir_shaken_attestation?: string;
    total_calls_30d?: number;
    score_breakdown?: { carrierBlocking?: { label?: string; stats?: { percentage?: number } | null } } | null;
  }[];
  attempts: { attempt_bucket: number; dials: number; connected: number }[];
  // local_hour is lead-local, converted server-side via the same stateToTimezone
  // util DialIQ's redial engine uses (DST-correct, one source of truth)
  hourly: { local_hour: number; dials: number; connected: number }[];
  // worked_on = distinct leads worked by ANY channel, ignoring assignment —
  // the only hygiene figure that is meaningful for a setter on a shared book.
  hygiene: { assigned: number; touched: number; never_called: number; stale_7d: number; missing_next_step: number; worked_on?: number };
  sources: { source: string; leads: number; sold: number; annual_premium: string | number }[];
  stageFlow?: {
    avgDaysToClose?: number | null;
    closedSamples?: number;
    avgDaysInStage: { stage: string; avgDays: number; samples: number }[];
    transitions: { pair: string; count: number }[];
  };
}

// Shared with AgentDetailsView (which renders the KPI tiles in its hero strip) —
// same queryKey means react-query dedupes to a single request.
export const useAgentPerformanceInsights = (
  agencyId: string,
  agentId: string,
  startDate?: string,
  endDate?: string,
  period?: string,
) =>
  useQuery<Insights>({
    queryKey: ["agent-performance-insights", agencyId, agentId, startDate, endDate, period],
    queryFn: async () => {
      const qs = new URLSearchParams();
      // period keeps this window identical to the hero tiles' stats window
      if (period) qs.set("period", period);
      if (startDate) qs.set("startDate", startDate);
      if (endDate) qs.set("endDate", endDate);
      const res: any = await handleApiCall(async () =>
        api.get({ url: getApiUrl(`/agencies/${agencyId}/agents/${agentId}/performance-insights${qs.toString() ? `?${qs}` : ""}`), auth: true })
      );
      return (res?.core ? res : res?.data) as Insights;
    },
    enabled: !!agencyId && !!agentId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000, // keep the dashboard live without a manual refresh
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

export const AgentPerformanceInsights = function ({
  agencyId,
  agentId,
  startDate,
  endDate,
  period,
}: {
  agencyId: string;
  agentId: string;
  startDate?: string;
  endDate?: string;
  period?: string;
}) {
  const { data, isLoading } = useAgentPerformanceInsights(agencyId, agentId, startDate, endDate, period);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // same scan the Settings "Numbers Health" page runs, executed for this agent
  const scanMutation = useMutation({
    mutationFn: async () => {
      const res: any = await handleApiCall(async () =>
        api.post({ url: getApiUrl(`/agencies/${agencyId}/agents/${agentId}/caller-id-scan`), auth: true, body: {} })
      );
      return res?.data ?? res;
    },
    onSuccess: (d: any) => {
      toast({ title: "Scan complete", description: `${d?.scanned ?? 0} number${(d?.scanned ?? 0) === 1 ? "" : "s"} checked${d?.failed ? `, ${d.failed} failed` : ""}` });
      queryClient.invalidateQueries({ queryKey: ["agent-performance-insights"] });
    },
    onError: (e: any) => {
      toast({ title: "Scan not run", description: e?.message || "Try again in a few minutes", variant: "destructive" });
    },
  });

  if (isLoading || !data?.core) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-7 w-16 rounded bg-gray-100 animate-pulse" />
                <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const { hygiene } = data;

  // Bucket the server's lead-local hours.
  // pg returns numerics as strings — coerce everything or the math NaNs out.
  const hourBuckets: { dials: number; connected: number }[] = Array.from({ length: 24 }, () => ({ dials: 0, connected: 0 }));
  for (const row of data.hourly || []) {
    const localHour = Number(row.local_hour);
    if (Number.isNaN(localHour) || localHour < 0 || localHour > 23) continue;
    hourBuckets[localHour].dials += Number(row.dials) || 0;
    hourBuckets[localHour].connected += Number(row.connected) || 0;
  }
  // Strict TCPA calling window: 8 AM – 9 PM lead-local. Dials outside it
  // (data-entry oddities) don't get chart real estate.
  const displayHours = Array.from({ length: 14 }, (_, i) => i + 8); // 8am–9pm
  const totalDials = displayHours.reduce((s, h) => s + hourBuckets[h].dials, 0);
  const maxRate = Math.max(0.01, ...displayHours.map((h) => (hourBuckets[h].dials > 0 ? hourBuckets[h].connected / hourBuckets[h].dials : 0)));

  // NOTE: the call KPIs (Dials / Conversations / Answer Rate / Leads Worked)
  // render in AgentDetailsView's hero strip alongside premium/policies/close
  // rate — this component is charts + hygiene + sources + stage flow only.

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Contact rate by attempt */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                Contact Rate by Attempt
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" aria-label="How this is calculated" className="inline-flex cursor-help items-center">
                      <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-72 text-xs leading-relaxed">
                    Every call to the same lead is numbered in order — 1st attempt, 2nd, 3rd… (5th and later grouped as 5th+).
                    Each bar shows what % of that attempt&apos;s dials became a real conversation (60+ seconds of talk —
                    ring-outs and voicemail blips don&apos;t count). Rising bars = follow-ups pay off.
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.attempts || []).length === 0 ? (
                <p className="text-sm text-gray-400 py-4">No dials in this period</p>
              ) : (
                (() => {
                  const rows = (data.attempts || []).map((a) => ({
                    bucket: Number(a.attempt_bucket),
                    dials: Number(a.dials) || 0,
                    connected: Number(a.connected) || 0,
                    rate: Number(a.dials) > 0 ? (Number(a.connected) / Number(a.dials)) * 100 : 0,
                  }));
                  const maxRate = Math.max(1, ...rows.map((r) => r.rate));
                  const best = rows.reduce((b, r) => (r.rate > b.rate ? r : b), rows[0]);
                  const label = (b: number) => (b >= 5 ? "5th+" : ["1st", "2nd", "3rd", "4th"][b - 1]);
                  const first = rows.find((r) => r.bucket === 1);
                  // one-line takeaway: is persistence paying off?
                  let insight: string | null = null;
                  if (first && best.bucket !== 1 && first.rate > 0 && best.rate / first.rate >= 1.3) {
                    insight = `${label(best.bucket)} calls connect ${(best.rate / first.rate).toFixed(1)}× better than 1st — follow-ups pay off`;
                  } else if (first && best.bucket === 1 && rows.length > 1) {
                    insight = "First calls connect best — lead lists are fresh, speed matters";
                  }
                  return (
                    <div>
                      <div className="space-y-2">
                        {rows.map((a, i) => {
                          const prev = i > 0 ? rows[i - 1] : null;
                          const delta = prev ? a.rate - prev.rate : null;
                          const isBest = a.bucket === best.bucket && a.rate > 0;
                          return (
                            <div key={a.bucket} className={`flex items-center gap-2.5 rounded-lg px-1.5 py-1 ${isBest ? "bg-primary/5 ring-1 ring-inset ring-primary/15" : ""}`}>
                              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${isBest ? "bg-primary text-primary-foreground" : "bg-gray-100 text-gray-500"}`}>
                                {label(a.bucket)}
                              </span>
                              <div className="relative h-5 flex-1 overflow-hidden rounded-full bg-gray-100">
                                <div
                                  className="flex h-full items-center justify-end rounded-full bg-gradient-to-r from-primary/50 to-primary pr-1.5 transition-all"
                                  style={{ width: `${Math.max(a.rate > 0 ? 9 : 3, (a.rate / maxRate) * 100)}%` }}
                                >
                                  {a.rate > 0 && (a.rate / maxRate) * 100 > 18 && (
                                    <span className="text-[9px] font-bold text-white tabular-nums">{a.rate.toFixed(0)}%</span>
                                  )}
                                </div>
                              </div>
                              <span className="w-16 shrink-0 text-right">
                                <span className="block text-[11px] font-semibold tabular-nums text-gray-800">{a.connected}/{a.dials}</span>
                                {delta != null && (
                                  <span className={`block text-[9px] font-medium tabular-nums ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-500" : "text-gray-400"}`}>
                                    {delta > 0 ? "▲" : delta < 0 ? "▼" : "•"} {Math.abs(delta).toFixed(0)}pts
                                  </span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {insight && (
                        <p className="mt-2.5 rounded-lg bg-gray-50 px-2.5 py-1.5 text-[11px] text-gray-600 ring-1 ring-inset ring-gray-500/5">
                          💡 {insight}
                        </p>
                      )}
                    </div>
                  );
                })()
              )}
            </CardContent>
          </Card>

          {/* Answer rate by lead-local hour */}
          <Card className="flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700">
                DialIQ · Best Time to Call{" "}
                <span className="font-normal text-gray-400">· lead's local time · TCPA window 8 AM – 9 PM</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col justify-center">
              {totalDials === 0 ? (
                <p className="text-sm text-gray-400 py-6">No dials inside the TCPA window (8 AM – 9 PM lead-local) yet</p>
              ) : (
              <>
              <div className="flex items-end gap-1 h-24">
                {displayHours.map((h) => {
                  const b = hourBuckets[h];
                  const rate = b.dials > 0 ? b.connected / b.dials : 0;
                  const height = b.dials > 0 ? Math.max(8, (rate / maxRate) * 100) : 4;
                  const peak = isDialIqPeak(h);
                  return (
                    <Tooltip key={h}>
                      <TooltipTrigger asChild>
                        <div className={`flex-1 h-full flex flex-col items-center justify-end gap-1 cursor-default rounded ${peak ? "bg-emerald-50/70" : ""}`}>
                          {/* px height, not % — the % height silently resolved to 0 inside the unsized flex column */}
                          <div
                            className={`w-full rounded-sm ${b.dials === 0 ? "bg-gray-100" : "bg-primary"} transition-all`}
                            style={{ height: `${Math.round((height / 100) * 72)}px`, opacity: b.dials === 0 ? 1 : 0.35 + 0.65 * (rate / maxRate) }}
                          />
                          <span className={`text-[9px] ${peak ? "text-emerald-600 font-medium" : "text-gray-400"}`}>{h > 12 ? h - 12 : h}{h >= 12 ? "pm" : "am"}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {(() => {
                          const h12 = `${h > 12 ? h - 12 : h} ${h >= 12 ? "PM" : "AM"}`;
                          const tail = peak ? " · DialIQ peak window" : "";
                          return b.dials === 0
                            ? `No dials at ${h12}${tail}`
                            : `${h12} lead-local — ${(rate * 100).toFixed(0)}% connect (${b.connected}/${b.dials})${tail}`;
                        })()}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-gray-400">
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-100" />
                DialIQ peak dial windows (8–10am · 3–5pm · 7–9pm)
              </div>
              </>
              )}
            </CardContent>
          </Card>

          {/* Dials by dialer mode — derived from predictive-session stamps +
              multi-line session linkage; everything else is a manual dial */}
          <Card className="flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700">Dials by Dialer Mode</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col justify-center">
              {!data.dialerModes || data.dialerModes.total === 0 ? (
                <p className="text-sm text-gray-400 py-4">No dials in this period</p>
              ) : (
                (() => {
                  const dm = data.dialerModes!;
                  const pods = [
                    { label: "Power Dialer", count: dm.powerDialer, ringClass: "stroke-violet-500", badgeClass: "bg-violet-500", icon: Zap },
                    { label: "Multi-Line", count: dm.multiLine, ringClass: "stroke-blue-500", badgeClass: "bg-blue-500", icon: Users },
                    { label: "Manual", count: dm.manual, ringClass: "stroke-gray-400", badgeClass: "bg-gray-400", icon: Hand },
                  ];
                  const topCount = Math.max(...pods.map((m) => m.count));
                  return (
                    <div className="grid grid-cols-3 gap-2">
                      {pods.map((m, i) => (
                        <DialerPod
                          key={m.label}
                          label={m.label}
                          count={m.count}
                          total={dm.total}
                          ringClass={m.ringClass}
                          badgeClass={m.badgeClass}
                          icon={m.icon}
                          delay={150 + i * 200}
                          isTop={m.count === topCount && m.count > 0}
                        />
                      ))}
                    </div>
                  );
                })()
              )}
            </CardContent>
          </Card>
        </div>

        {/* Self-uploaded leads — the side book that never went through the
            owner's assignment flow. Unassigned ones are invisible to every
            assigned-book view on this page, so they get called out here. */}
        {(() => {
          const su = data.selfUploads;
          const received = su?.receivedFrom || [];
          if (!su || (Number(su.total) === 0 && received.length === 0)) return null;
          const unassigned = Number(su.unassigned) || 0;
          const warm = unassigned > 0;
          return (
            <Card className={warm ? "border-amber-200 bg-amber-50/40" : ""}>
              <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3.5">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <UploadCloud className={`h-4 w-4 ${warm ? "text-amber-500" : "text-gray-400"}`} />
                  Self-Uploaded Leads
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label="What is this" className="inline-flex cursor-help items-center">
                        <Info className="h-3.5 w-3.5 text-primary/70 hover:text-primary transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-72 text-xs leading-relaxed">
                      Leads this agent uploaded/created themselves instead of receiving through assignment.
                      Unassigned ones don&apos;t appear in the assigned-lead views on this page — this is the
                      only place they&apos;re counted.
                    </TooltipContent>
                  </Tooltip>
                </span>
                <span className="text-sm tabular-nums text-gray-700">
                  <span className="font-bold">{Number(su.total).toLocaleString()}</span> self-uploaded
                </span>
                {su.last_upload && (
                  <span className="ml-auto text-xs text-gray-400">
                    Last upload {new Date(su.last_upload).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
                {received.length > 0 && (
                  <span className="flex w-full flex-wrap items-center gap-1.5 border-t border-gray-200/60 pt-2 text-xs">
                    <span className="text-gray-400">Assigned leads received from:</span>
                    {received.map((r) => (
                      <span key={r.name} className="rounded-full bg-white px-2 py-0.5 font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10">
                        {r.name} · {Number(r.count).toLocaleString()}
                      </span>
                    ))}
                  </span>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Geographic heat map — leads / policies / calls by lead state */}
        <AgentActivityMap geo={data.geo} />

        {/* Caller ID health: this agent's numbers, worst first */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-sm font-semibold text-gray-700">
                Caller ID health <span className="font-normal text-gray-400">· this agent&apos;s numbers</span>
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                disabled={scanMutation.isPending}
                onClick={() => scanMutation.mutate()}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${scanMutation.isPending ? "animate-spin" : ""}`} aria-hidden />
                {scanMutation.isPending ? "Rescanning…" : "Rescan"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {(data.callerIdHealth || []).length === 0 ? (
              <p className="py-4 text-sm text-gray-400">No active numbers for this agent yet.</p>
            ) : (
              <ul className="max-h-[340px] divide-y divide-gray-100 overflow-y-auto">
                {(data.callerIdHealth || []).map((n) => {
                  const status = (n.health_status || "unknown").toLowerCase();
                  const statusLabel = status === "unknown" ? "Not scored" : status.charAt(0).toUpperCase() + status.slice(1);
                  const statusTone =
                    status === "excellent" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : status === "good" ? "border-green-200 bg-green-50 text-green-700"
                    : status === "fair" ? "border-amber-200 bg-amber-50 text-amber-700"
                    : status === "poor" ? "border-red-200 bg-red-50 text-red-700"
                    : "border-gray-200 bg-gray-50 text-gray-500";
                  const rate = n.answer_rate_30d != null ? Number(n.answer_rate_30d) : null;
                  const calls30 = Number(n.total_calls_30d) || 0;
                  const spam = Number(n.spam_flag_count) || 0;
                  const att = (n.stir_shaken_attestation || "").toUpperCase();
                  const blocking = n.score_breakdown?.carrierBlocking;
                  const blocked = blocking?.label === "High" || blocking?.label === "Severe";
                  return (
                    <li key={n.phone_number} className="grid grid-cols-1 gap-2 py-2.5 sm:grid-cols-[9.5rem_7.5rem_1fr] sm:items-center sm:gap-4">
                      <span className="text-sm font-medium tabular-nums text-gray-900">{formatPhoneNumber(n.phone_number)}</span>
                      <span className={`w-fit rounded-md border px-2 py-0.5 text-xs font-medium tabular-nums ${statusTone}`}>
                        {status === "unknown" ? statusLabel : `${Number(n.health_score) || 0} · ${statusLabel}`}
                      </span>
                      <div className="min-w-0">
                        {/* Typical number ≈ 6%; the bar is full at 15% (the top tenth). */}
                        <div className="flex justify-between text-[11px] text-gray-500">
                          <span>Reached a person, 30 days</span>
                          <span className="font-semibold tabular-nums text-gray-700">
                            {calls30 > 0 && rate != null ? `${rate.toFixed(1)}%` : "No calls"}
                          </span>
                        </div>
                        <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                          <div
                            className={`h-full rounded-full ${rate != null && rate >= 6 ? "bg-emerald-500" : rate != null && rate >= 4 ? "bg-amber-500" : "bg-red-400"}`}
                            style={{ width: `${Math.min(100, Math.max(((rate || 0) / 15) * 100, rate ? 3 : 0))}%` }}
                          />
                        </div>
                        <p className="mt-1 text-[11px] text-gray-500">
                          {calls30.toLocaleString()} dials
                          {spam > 0 ? (
                            <span className="font-medium text-red-600"> · {spam} spam flag{spam > 1 ? "s" : ""}</span>
                          ) : blocked ? (
                            <span className={`font-medium ${blocking?.label === "Severe" ? "text-red-600" : "text-amber-700"}`}>
                              {" "}· {blocking?.stats?.percentage}% straight to voicemail
                            </span>
                          ) : (
                            " · no spam flags"
                          )}
                          {att && ` · caller ID ${att === "A" ? "verified" : att === "B" ? "partly verified" : "gateway only"} (${att})`}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

      </div>
    </TooltipProvider>
  );
};


/**
 * Per-agent geographic heat map — same USMapSVG the agency-wide Policy Map
 * uses, scoped to this agent's book: leads assigned, policies/premium, and
 * calls (period-scoped) by lead state.
 */
export function AgentActivityMap({ geo }: { geo?: Insights["geo"] }) {
  const [view, setView] = useState<"leads" | "policies" | "calls">("leads");
  const rows = geo || [];
  const data: StateData[] = rows
    .map((r) => ({
      state: r.state,
      count: view === "leads" ? Number(r.leads) || 0 : view === "policies" ? Number(r.policies) || 0 : Number(r.calls) || 0,
      totalPremium: view === "policies" ? Number(r.premium) || 0 : 0,
      topCarrier: "",
    }))
    .filter((d) => d.count > 0);
  const maxCount = Math.max(1, ...data.map((d) => d.count));
  const total = data.reduce((s, d) => s + d.count, 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <MapPin className="h-4 w-4 text-gray-400" /> Activity Heat Map
            <span className="font-normal text-gray-400">· by lead's state</span>
          </CardTitle>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {([
              { key: "leads", label: "Leads" },
              { key: "policies", label: "Policies Sold" },
              { key: "calls", label: "Calls" },
            ] as const).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setView(t.key)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  view === t.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-400">No {view === "policies" ? "policies" : view} with lead state data yet</p>
        ) : (
          <>
            <USMapSVG data={data} maxCount={maxCount} itemLabel={view === "policies" ? "policy" : view === "calls" ? "call" : "lead"} />
            <p className="mt-2 text-center text-xs text-gray-400">
              {total.toLocaleString()} {view === "policies" ? "policies" : view} across {data.length} state{data.length === 1 ? "" : "s"}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
